import { InputBaseProps, styled, TextFieldProps } from "@mui/material";
import { useGridApiContext } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { InputBase } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { GridEditDateCellProps } from "./buyerListingTypes";
import { DateView } from "@mui/x-date-pickers/models";
import { useEffect, useState } from "react";
import { INVALID_DATE, INVALID_FUTURE_DATE } from "../../../utils/common/validator";
import { StyledTooltip } from "./buyerListingGridEditText";


const GridEditDateInput = styled(InputBase)({
  fontSize: "inherit",
  padding: "0 9px",
});

/**
 * This component wraps `GridEditDateInput`.
 * @summary This component renders a `GridEditDateInput` with additional properties spread onto it.
 * @param {TextFieldProps} props - The properties to apply to the `GridEditDateInput`.
 * @returns {JSX.Element} - Returns the `GridEditDateInput` component with the applied props.
 */
function WrappedGridEditDateInput(props: TextFieldProps) {
  const { InputProps, focused, ...other } = props;
  return (
    <GridEditDateInput
      fullWidth
      {...InputProps}
      {...(other as InputBaseProps)}
    />
  );
}

/**
 * @summary This component renders a date picker for editing date cells in a grid.
 * @param {string | number} props.id - The homeSiteId.
 * @param {string} props.field - The field name.
 * @param {Dayjs | null} props.value - The current value of the cell, represented as a Dayjs object or null if no value is set.
 * @param {(id: string, field: string, date: string,params: GridCellParams) => Promise<void>} props.handleChangeDates - A function to handle the date change event, which updates the date with the relevant service.
 * @param {any} props.params - The params object containing additional information about the cell.
 * @returns {JSX.Element} - Returns a `DatePicker` component.
 */

export function GridEditDateCell({
  id,
  field,
  value,
  params,
  handleChangeDates,
  disablePast = false,
  disableFuture = false,
  minDate,
  openCalendarOnMount = false,
  controlledOpen,
  onRequestClose,
  onBeforeAccept,
  interceptAccept,
}: GridEditDateCellProps) {
  const apiRef = useGridApiContext();
  const [showError, setShowError] = useState(false);
  const [showText, setShowText] = useState("");
  const [ error, setError] = useState<boolean>(false);
  const [calendarOpen, setCalendarOpen] = useState(openCalendarOnMount);
  const isControlled = controlledOpen !== undefined;
  const ERROR_DISPLAY_DURATION = 5000;

    /** Clear the error tooltip from the view after a specified duration */
  useEffect(() => {
    if (showError) {
      const timer = setTimeout(
        () => setShowError(false),
        ERROR_DISPLAY_DURATION
      );
      return () => clearTimeout(timer);
    }
  }, [showError]);

  const handleClose = async (value: Dayjs | null) => {
    const storeDate = value ? dayjs(value).isValid() ? dayjs(value).format("YYYY-MM-DD") : "" : "";
    if (onBeforeAccept) {
      const shouldCommit = await Promise.resolve(onBeforeAccept(storeDate));
      if (!shouldCommit) return;
    }
    setResult(storeDate);
    try {
      handleChangeDates(id, field, storeDate || "", params);
    } catch (err) {
      console.error("Failed to handle date change:", err);
    }
  };

  function setCellValue(v: string) {
    apiRef.current.setEditCellValue({
      id,
      field,
      value: v,
    });
  }

  function setResult(v: string) {
    setCellValue(v);
    apiRef.current.stopCellEditMode({
      id,
      field,
    });
  }

  function handleOnCancel() {
    const updateValue = show(dayjs(value));
    setResult(updateValue);
    // setOpen(false);
  }

  const pickerViews: readonly DateView[] = [
    "year",
    "month",
    "day",
  ];

  return (
    <StyledTooltip open={showError} title={showText}>
      <div>

    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
      <DatePicker
        views={pickerViews}
        value={value && dayjs(value).isValid() ? dayjs(value) : null}
        {...(isControlled
          ? { open: controlledOpen, onClose: onRequestClose }
          : openCalendarOnMount
            ? { open: calendarOpen, onClose: () => setCalendarOpen(false) }
            : {}
        )}
        autoFocus
        onAccept={(value: Dayjs | null) => {
          // interceptAccept runs synchronously before any async code in handleClose
          if (interceptAccept) {
            const storeDate = value && dayjs(value).isValid() ? dayjs(value).format("YYYY-MM-DD") : "";
            if (!interceptAccept(storeDate)) return; // blocked — skip commit entirely
          }
          if (openCalendarOnMount) setCalendarOpen(false);
          handleClose(value);
        }} 
        onError={(error) => {
          switch (error) {
          case "invalidDate":
            setShowError(true);
            setShowText(INVALID_DATE);
            setError(true);
            break;
          case "disableFuture":
            setShowError(true);
            setShowText(INVALID_FUTURE_DATE);
            setError(true);
            break;
          case null:
            setShowError(false);
            setShowText("");
            setError(false);
            break;
          }
        }}
        slots={{
          textField: WrappedGridEditDateInput,
         }}
         slotProps={{
          desktopTrapFocus: {
            /**
             * When the user clicks a calendar navigation arrow, focus briefly
             * enters the FocusTrap, setting activated=true. After the Delayed
             * Reason panel appears, the FocusTrap would steal focus back from
             * any element outside the Popper (including our portal TextField).
             * disableEnforceFocus prevents that redirection so the user can
             * interact with panel elements freely.
             */
            disableEnforceFocus: true,
          },
          textField: {
            onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
              if (event.relatedTarget && event.relatedTarget.tagName !== "DIV") {
                return;
              }
              const raw = event.target.value.trim();
              if (raw === "MM-DD-YYYY" || raw === "") {
                handleClose(null);
                return
              }
              const eValue = dayjs(raw);
              if (eValue.isValid() && !error) {
                handleClose(eValue);
              } else {
                handleClose(dayjs(value));
              }
            },
            onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
              const raw = (event.target as HTMLInputElement).value.trim();
              if (event.key === "Enter" || event.key === "Tab") {
                if (raw === "MM-DD-YYYY" || raw === "") {
                  handleClose(null);
                  return;
                }
                const eValue = dayjs(raw);
                if (eValue.isValid() && !error) {
                  // setCellValue(eValue.format("MM-DD-YYYY"));
                  handleClose(eValue);
                } else {
                  // setCellValue(value);
                }
              } else if (event.key === "Escape") {
                handleOnCancel();
              }
            }
          },
          actionBar: { actions: ["clear"],
            onClear: () => {
              setResult("");
              handleChangeDates(id, field, "", params);
              if (openCalendarOnMount) setCalendarOpen(false);
            }
           },
        }}
        format="MM-DD-YYYY"
        minDate={minDate ? dayjs(minDate) : undefined}
        disablePast={disablePast}
        disableFuture={disableFuture}

      />
    </LocalizationProvider>
      </div>
    </StyledTooltip>
  );
}

function show(newValue: dayjs.Dayjs | null): string{
  if (!newValue) {
    return "";
  }
  if (!dayjs.isDayjs(newValue)) {
    newValue = dayjs(newValue);
  }
  if (newValue && newValue.isValid()) {
    return newValue.format("YYYY-MM-DD");
  }
  return "";
}

